import { db } from '@/firebase/init';
import { collection, getDocs } from 'firebase/firestore';

/**
 * Service for retrieving bus route data from Firestore
 */
export default {
  /**
   * Get all bus routes from Firestore
   * @returns {Promise<Array>} - Array of route objects
   */
  async getAllRoutes() {
    try {
      const routesCollection = collection(db, 'routes');
      const routeSnapshot = await getDocs(routesCollection);
      
      const routes = [];
      routeSnapshot.forEach((doc) => {
        routes.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return routes;
    } catch (error) {
      console.error('Error fetching bus routes:', error);
      throw error;
    }
  },
  
  /**
   * Extract all unique campus names from the routes
   * @param {Array} routes - Array of route objects
   * @returns {Array} - Array of unique campus names
   */
  getUniqueCampuses(routes) {
    const campusSet = new Set();
    
    routes.forEach(route => {
      // Add pickup locations
      if (route.pickup_locations) {
        route.pickup_locations.forEach(location => {
          // Extract campus name from location string
          const campusName = this.extractCampusName(location);
          if (campusName) campusSet.add(campusName);
        });
      }
      
      // Add dropoff locations
      if (route.dropoff_locations) {
        route.dropoff_locations.forEach(location => {
          // Extract campus name from location string
          const campusName = this.extractCampusName(location);
          if (campusName) campusSet.add(campusName);
        });
      }
    });
    
    return Array.from(campusSet).sort();
  },
  
  /**
   * Extract campus name from a location string
   * @param {String} locationString - Location string like "Hatfield Campus – Location"
   * @returns {String|null} - Extracted campus name or null
   */
  extractCampusName(locationString) {
    // Common campus names to extract
    const campusPatterns = [
      { pattern: /Hatfield/i, name: 'Hatfield' },
      { pattern: /Groenkloof/i, name: 'Groenkloof' },
      { pattern: /Prinshof/i, name: 'Prinshof' },
      { pattern: /Onderstepoort/i, name: 'Onderstepoort' },
      { pattern: /Hillcrest/i, name: 'Hillcrest' },
      { pattern: /Mamelodi/i, name: 'Mamelodi' },
      { pattern: /Future Africa/i, name: 'Future Africa' },
      { pattern: /Kalafong/i, name: 'Kalafong' },
      { pattern: /Sunnyside|Arcadia/i, name: 'Sunnyside/Arcadia' },
    ];
    
    for (const { pattern, name } of campusPatterns) {
      if (pattern.test(locationString)) {
        return name;
      }
    }
    
    return null;
  },
  
  /**
   * Get today's service schedule for a route
   * @param {Object} route - Route object
   * @returns {Object} - Object with service times for today
   */
  getTodaySchedule(route) {
    const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayName = days[today];
    
    // Check if service is available today
    const hasServiceToday = route.days && route.days.includes(todayName);
    if (!hasServiceToday) {
      return { available: false, reason: 'No service today' };
    }
    
    // Get schedule for today
    const schedule = {};
    if (route.schedule) {
      Object.keys(route.schedule).forEach(direction => {
        const directionSchedule = route.schedule[direction];
        
        // Check for specific day schedule or weekday schedule
        if (directionSchedule[todayName]) {
          schedule[direction] = directionSchedule[todayName];
        } else if (today >= 1 && today <= 5 && directionSchedule['Monday-Friday']) {
          schedule[direction] = directionSchedule['Monday-Friday'];
        } else if ((today === 0 || today === 6) && directionSchedule['Saturday-Sunday']) {
          schedule[direction] = directionSchedule['Saturday-Sunday'];
        } else if (today === 6 && directionSchedule['Saturday']) {
          schedule[direction] = directionSchedule['Saturday'];
        } else if (today === 0 && directionSchedule['Sunday']) {
          schedule[direction] = directionSchedule['Sunday'];
        }
      });
    }
    
    return { available: true, schedule };
  },
  
  /**
   * Calculate time until the next bus
   * @param {Array} times - Array of time strings in format "HH:MM"
   * @returns {Number|null} - Minutes until next bus or null if no more buses today
   */
  getTimeUntilNextBus(times) {
    if (!times || times.length === 0) {
      return null;
    }
    
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeMinutes = (currentHours * 60) + currentMinutes;
    
    // Convert all times to minutes since midnight
    const timeInMinutes = times.map(time => {
      const [hours, minutes] = time.split(':').map(Number);
      return (hours * 60) + minutes;
    });
    
    // Find the next time that is greater than the current time
    const nextTimeIndex = timeInMinutes.findIndex(time => time > currentTimeMinutes);
    
    if (nextTimeIndex === -1) {
      return null; // No more buses today
    }
    
    // Calculate minutes until next bus
    return timeInMinutes[nextTimeIndex] - currentTimeMinutes;
  }
};
